import { XIcon } from "@phosphor-icons/react/dist/csr/X";
import dayjs from "dayjs";
import { useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { textVariants } from "@volca/ui/components/ui/text";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@volca/ui/components/ui/tooltip";
import { cn } from "@volca/ui/lib/utils";
import { extractErrorMessage } from "@/core/api/errors";
import { DeleteDocumentModal } from "./delete-document-modal";
import { DocumentIconFor } from "./document-icon";
import { formatFileSize } from "./document-utils";
import { useDeleteOnboardingDocument } from "./query";
import type { OnboardingDocument } from "./types";

interface DocumentListProps {
  businessId: string;
  documents: OnboardingDocument[];
}

export const DocumentList = ({ businessId, documents }: DocumentListProps) => {
  const [pendingDelete, setPendingDelete] = useState<OnboardingDocument | null>(
    null,
  );
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const deleteMutation = useDeleteOnboardingDocument(businessId);

  const closeModal = () => {
    setPendingDelete(null);
    setDeleteError(null);
  };

  const confirmDelete = () => {
    if (!pendingDelete) return;

    setDeleteError(null);
    deleteMutation.mutate(pendingDelete.id, {
      onSuccess: closeModal,
      onError: (error) => {
        setDeleteError(extractErrorMessage(error).message);
      },
    });
  };

  return (
    <>
      {/* Its own provider, so the list works wherever it is rendered rather than only
          under the app shell's. Nested providers are fine; this one just sets a shorter
          delay than a page-level tooltip wants. */}
      <TooltipProvider delay={200}>
        <Stack className="w-full" gap={0} align="stretch">
          {documents.map((document) => {
            const details = [
              document.content_type,
              formatFileSize(document.file_size),
            ].filter(Boolean);
            const name = document.file_name || document.title;

            return (
              <Stack
                key={document.id}
                direction="row"
                align="center"
                gap={4}
                className="w-full border-b border-line py-3 last:border-b-0"
              >
                <DocumentIconFor
                  contentType={document.content_type}
                  fileName={name}
                  className="h-5 w-5 shrink-0 text-subtle"
                />
                {/* align stretch, or the flex column sizes to its longest child and a
                  generated filename runs straight through the delete button. */}
                <Stack gap={0} align="stretch" className="min-w-0 flex-1">
                  {/* A generated filename is wider than the row, so it truncates. The full
                    name stays readable on hover: a native title tooltip is slow and easy
                    to miss, and the truncation is visual only, so assistive tech still
                    gets the whole string from the element itself. */}
                  <Tooltip>
                    <TooltipTrigger
                      render={
                        <span
                          className={cn(
                            textVariants({ variant: "body" }),
                            "block cursor-default truncate text-left",
                          )}
                        />
                      }
                    >
                      {name}
                    </TooltipTrigger>
                    <TooltipContent className="max-w-lg break-all">
                      {name}
                    </TooltipContent>
                  </Tooltip>
                  <Text variant="caption" tone="muted">
                    {[
                      dayjs(document.uploaded_at).format("MMM D [at] h:mm A"),
                      ...details,
                    ].join(" · ")}
                  </Text>
                </Stack>
                {document.deletable && (
                  <Button
                    variant="ghost"
                    color="danger"
                    size="icon-sm"
                    aria-label={`Delete ${name}`}
                    onClick={() => {
                      setDeleteError(null);
                      setPendingDelete(document);
                    }}
                  >
                    <XIcon />
                  </Button>
                )}
              </Stack>
            );
          })}
        </Stack>
      </TooltipProvider>

      <DeleteDocumentModal
        document={pendingDelete}
        isDeleting={deleteMutation.isPending}
        error={deleteError}
        onCancel={closeModal}
        onConfirm={confirmDelete}
      />
    </>
  );
};
